
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import PrivateRoute from './PrivateRouting';
import ProtectedLoginRoute from './ProtectedLoginRoute';
import { RoutesLink } from '@/utility/RouteLinks';
import Login from '@/pages/auth/Login';
import SuperAdminDashboard from '@/pages/SuperAdmin/Dashboard/SuperAdminDashboard';
import Forgot from '@/pages/auth/Forgot';
import VerifyOtp from '@/pages/auth/VerifyOtp';
import ResetPassword from '@/pages/auth/ResetPassword';
import Employees from '@/pages/SuperAdmin/Employee/Employees';
import CreateEmployee from '@/pages/SuperAdmin/Employee/CreateEmployee';
import Department from '@/pages/SuperAdmin/Department/Department';
import AddDepartment from '@/pages/SuperAdmin/Department/AddDepartment';
import Branch from '@/pages/SuperAdmin/Branch/Branch';
import AddBranch from '@/pages/SuperAdmin/Branch/AddBranch';
import Company from '@/pages/SuperAdmin/Company/Company';
import CreateCompany from '@/pages/SuperAdmin/Company/CreateCompany';
import ShiftAndTime from '@/pages/SuperAdmin/ShiftAndTime/ShifAndTime';
import AddShiftAndTime from '@/pages/SuperAdmin/ShiftAndTime/AddShiftAndTime';
import Designation from '@/pages/SuperAdmin/Designation/Designation';
import AddDesignation from '@/pages/SuperAdmin/Designation/AddDesignation';
import SsoLogin from '@/pages/auth/SsoLogin';
import Site from '@/pages/SuperAdmin/Site/Site';
import AddSite from '@/pages/SuperAdmin/Site/AddSite';
// import CampusConfig from '@/pages/SuperAdmin/Campus/CampusConfig';
import AddCampus from '@/pages/SuperAdmin/Campus/AddCampus';
import CampusConfig from '@/pages/SuperAdmin/Campus/CampusConfig';
import Application from '@/pages/SuperAdmin/Applications/Application';
import AddApplication from '@/pages/SuperAdmin/Applications/AddApplication';
import Profile from '@/pages/SuperAdmin/Profile/Profile';
import EditProfile from '@/pages/SuperAdmin/Profile/EditProfile';
import EmployeeDetail from '@/pages/SuperAdmin/Employee/EmployeeDetail';
import LeaveSetting from '@/pages/SuperAdmin/HrmsSettings/LeaveSettings/LeaveSettings';
import AddLeaveSetting from '@/pages/SuperAdmin/HrmsSettings/LeaveSettings/AddLeaveSetting';
import AddLeave from '@/pages/SuperAdmin/Leave/AddLeave';
import LeaveDetail from '@/pages/SuperAdmin/Leave/LeaveDetail';
import TeamLeave from '@/pages/SuperAdmin/Leave/TeamLeave';
import MyLeave from '@/pages/SuperAdmin/Leave/MyLeave';
import UserDashboard from '@/pages/Dashboard/UserDashboard';
import MyAttendance from '@/pages/SuperAdmin/Attendance/MyAttendance';
import TeamAttendance from '@/pages/SuperAdmin/Attendance/TeamAttendance';
import WFHRequest from '@/pages/SuperAdmin/WFH/WFHRequest';
import WFHTeam from '@/pages/SuperAdmin/WFH/WFHTeam';
import ADDWFH from '@/pages/SuperAdmin/WFH/ADDWFH';
import WfhDetail from '@/pages/SuperAdmin/WFH/WfhDetail';
import MyOd from '@/pages/SuperAdmin/Od/MyOd';
import TeamOd from '@/pages/SuperAdmin/Od/TeamOd';
import Payslip from '@/pages/SuperAdmin/Payslip/Payslip';
import PayslipDetail from '@/pages/SuperAdmin/Payslip/PayslipDetail';
import OdDetail from '@/pages/SuperAdmin/Od/OdDetail';
import CreateOd from '@/pages/SuperAdmin/Od/CreateOd';
import AttendanceLog from '@/pages/SuperAdmin/Attendance/AttendaceLog';

import { useSelector } from 'react-redux';

import type { RootState } from '@/store/store';
import { permissionKeys } from '@/utility/helper';

interface IPrivateRoutes {
    path: string;
    route: React.ReactNode;
    accessKey?: string;
    teamAccess?: boolean;
}

interface IAccessGuard {
    element: React.ReactNode;
    accessKey?: string;
    teamAccess?: boolean;
}

const AccessGuard: React.FC<IAccessGuard> = ({ element, accessKey, teamAccess }) => {
    // const dispatch = useAppDispatch();
    const { user: userDetails, } = useSelector((state: RootState) => state.user);

    // Admins bypass designation access restrictions
    const isAdmin = userDetails?.type === 'admin' || userDetails?.type === 'super_admin';
    if (isAdmin) {
        return <>{element}</>;
    }

    // Check specific access keys (e.g. attendance, leave, wfh)
    if (accessKey) {
        const menuBar = userDetails?.designation?.menuBar?.keyNames;
        const hasAccessKey = Array.isArray(menuBar) && menuBar.includes(accessKey);
        if (!hasAccessKey) {
            return <Navigate to={RoutesLink.dashboard} replace />;
        }
    }

    // Check team access restrictions
    if (teamAccess && !userDetails?.designation?.canAccessTeam) {
        return <Navigate to={RoutesLink.dashboard} replace />;
    }

    return <>{element}</>;
};

export default function CommonRouts() {

    const ProtectedLoginRoutes = [

        {
            path: RoutesLink?.authRoutes?.login,
            route: <Login />,
        },
        {
            path: RoutesLink?.authRoutes?.forgot,
            route: <Forgot />,
        },
        {
            path: RoutesLink?.authRoutes?.verifyOtp,
            route: <VerifyOtp />,
        },
        {
            path: RoutesLink?.authRoutes?.resetPassword,
            route: <ResetPassword />,
        },



    ];

    const privateRoutes: IPrivateRoutes[] = [

        {
            path: RoutesLink.superAdminDashboard,
            route: <SuperAdminDashboard />,
        },
        {
            path: RoutesLink.userDashboard,
            route: <UserDashboard />,
        },
        {
            path: RoutesLink.superAdminEmployees,
            route: <Employees />,
        },
        {
            path: RoutesLink.superAdminEmplyoeeOnboard,
            route: <Employees />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.employeeDetail}/:id`,
            route: <EmployeeDetail />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.createEmployee,
            route: <CreateEmployee />,
        },
        {
            path: `${RoutesLink.editEmployee}/:id`,
            route: <CreateEmployee />,
        },
        // Profile
        {
            path: RoutesLink.profile,
            route: <Profile />,
        },
        {
            path: RoutesLink.editProfile,
            route: <EditProfile />,
        },
        {
            path: RoutesLink.superAdminDepartment,
            route: <Department />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addDepartment,
            route: <AddDepartment />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editDepartment}/:id`,
            route: <AddDepartment />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.superAdminBranch,
            route: <Branch />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addBranch,
            route: <AddBranch />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editBranch}/:id`,
            route: <AddBranch />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.company,
            route: <Company />,
            accessKey: permissionKeys.company,
        },
        {
            path: RoutesLink.createCompany,
            route: <CreateCompany />,
            accessKey: permissionKeys.company,
        },
        {
            path: `${RoutesLink.editCompany}/:id`,
            route: <CreateCompany />,
            accessKey: permissionKeys.company,
        },
        {
            path: RoutesLink.shiftAndTime,
            route: <ShiftAndTime />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addShiftAndTime,
            route: <AddShiftAndTime />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editShiftAndTime}/:id`,
            route: <AddShiftAndTime />,
            accessKey: permissionKeys.hrmSetting,
        },
        //   designation
        {
            path: RoutesLink.superAdminDesignation,
            route: <Designation />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addDesignation,
            route: <AddDesignation />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editDesignation}/:id`,
            route: <AddDesignation />,
            accessKey: permissionKeys.hrmSetting,
        },
        // Site
        {
            path: RoutesLink.superAdminSite,
            route: <Site />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addSite,
            route: <AddSite />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editSite}/:id`,
            route: <AddSite />,
            accessKey: permissionKeys.hrmSetting,
        },
        // campus
        {
            path: RoutesLink.superAdminCampus,
            route: <CampusConfig />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addCampus,
            route: <AddCampus />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editCampus}/:id`,
            route: <AddCampus />,
            accessKey: permissionKeys.hrmSetting,
        },
        // applications
        {
            path: RoutesLink.superAdminApplication,
            route: <Application />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addApplication,
            route: <AddApplication />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editApplication}/:id`,
            route: <AddApplication />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.profile,
            route: <Profile />,
        },
        {
            path: RoutesLink.editProfile,
            route: <EditProfile />,
        },
        // Leave Setting
        {
            path: RoutesLink.leaveSetting,
            route: <LeaveSetting />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: RoutesLink.addLeaveSetting,
            route: <AddLeaveSetting />,
            accessKey: permissionKeys.hrmSetting,
        },
        {
            path: `${RoutesLink.editLeaveSetting}/:id`,
            route: <AddLeaveSetting />,
            accessKey: permissionKeys.hrmSetting,
        },
        // Leave
        {
            path: RoutesLink.addLeave,
            route: <AddLeave />,
            accessKey: permissionKeys.leave,
        },
        {
            path: `${RoutesLink.editLeave}/:id`,
            route: <AddLeave />,
            accessKey: permissionKeys.leave,
        },
        {
            path: `${RoutesLink.leaveDetail}/:id`,
            route: <LeaveDetail />,
            accessKey: permissionKeys.leave,
        },
        {
            path: `${RoutesLink.teamLeaveDetail}/:id`,
            route: <LeaveDetail />,
            accessKey: permissionKeys.leave,
            teamAccess: true,
        },
        {
            path: RoutesLink.teamLeave,
            route: <TeamLeave />,
            accessKey: permissionKeys.leave,
            teamAccess: true,
        },
        {
            path: RoutesLink.myLeave,
            route: <MyLeave />,
            accessKey: permissionKeys.leave,
        },
        // attendance
        {
            path: RoutesLink.myAttendance,
            route: <MyAttendance />,
            accessKey: permissionKeys.attendance,
        },
        {
            path: RoutesLink.teamAttendance,
            route: <TeamAttendance />,
            accessKey: permissionKeys.attendance,
            teamAccess: true,
        },
        {
            path: RoutesLink.attendanceLog,
            route: <AttendanceLog />,
            accessKey: permissionKeys.attendance,
        },
        // WFH
        {
            path: RoutesLink.wfhRequest,
            route: <WFHRequest />,
            accessKey: permissionKeys.wfh,
        },
        {
            path: RoutesLink.teamWfh,
            route: <WFHTeam />,
            accessKey: permissionKeys.wfh,
            teamAccess: true,
        },
        {
            path: RoutesLink.addWfh,
            route: <ADDWFH />,
            accessKey: permissionKeys.wfh,
        },
        {
            path: `${RoutesLink.wfhDetail}/:id`,
            route: <WfhDetail />,
            accessKey: permissionKeys.wfh,
        },
        {
            path: `${RoutesLink.teamWfhDetail}/:id`,
            route: <WfhDetail />,
            accessKey: permissionKeys.wfh,
            teamAccess: true,
        },
        // OD
        {
            path: RoutesLink.myOd,
            route: <MyOd />,
            accessKey: permissionKeys.od,
        },
        {
            path: RoutesLink.teamOd,
            route: <TeamOd />,
            accessKey: permissionKeys.od,
            teamAccess: true,
        },
        {
            path: RoutesLink.createOd,
            route: <CreateOd />,
            accessKey: permissionKeys.od,
        },
        {
            path: `${RoutesLink.odDetail}/:id`,
            route: <OdDetail />,
            accessKey: permissionKeys.od,
        },
        {
            path: `${RoutesLink.teamOdDetail}/:id`,
            route: <OdDetail />,
            accessKey: permissionKeys.od,
            teamAccess: true,
        },
        // Payslip
        {
            path: RoutesLink.payslip,
            route: <Payslip />,
            accessKey: permissionKeys.payslip,
        },
        {
            path: RoutesLink.payslipDetail,
            route: <PayslipDetail />,
            accessKey: permissionKeys.payslip,
        }

        
    ];


    const location = useLocation();

    return (
        <>
            <Routes>
                {ProtectedLoginRoutes.map((route, index) => (
                    <Route
                        key={`login-${index}`}
                        path={route.path}
                        element={<ProtectedLoginRoute element={route.route} />}
                    />
                ))}

                {/* All Private Routes — generated per role */}
                {privateRoutes.map((route, index) => (
                    <Route
                        key={`private-${index}`}
                        path={route.path}
                        element={
                            <PrivateRoute
                                element={
                                    <AccessGuard
                                        element={route.route}
                                        accessKey={route.accessKey}
                                        teamAccess={route.teamAccess}
                                    />
                                }
                            />
                        }
                    />
                ))}

                {/* Public standalone route — accessible regardless of auth state */}
                <Route path={RoutesLink.authRoutes.ssoLogin} element={<SsoLogin />} />

                {/* Global Fallback */}
                <Route path="*" element={<Navigate to={{ pathname: RoutesLink.authRoutes.login, search: location.search }} replace />} />
            </Routes>
        </>
    );
}
